import { useTranslation } from 'react-i18next';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import CreditsFooter from './CreditsFooter';
import styles from './AiFieldAssist.module.scss';

type Props = {
  label: string;
  // "No suggestions found" (live only) offers Dismiss only — regenerating
  // won't help unless the file changes. Failed offers Dismiss & Retry.
  showRetry: boolean;
  onCancel: () => void;
  onRetry: () => void;
  creditsRemaining?: AiCredits | null;
  // Set while the company's AI budget refuses spend. Dismiss stays available —
  // settling a failed annotation costs nothing — but a retry would fail
  // identically, so it is disabled and the footer says why.
  blockedText?: string | null;
};

// ── Failed / No suggestions found — same red affordance, different copy +
// actions ──
export default function ErrorContent({
  label,
  showRetry,
  onCancel,
  onRetry,
  creditsRemaining,
  blockedText,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className={styles.popoverBody}>
      <div className={styles.popoverHeader}>
        <div className={styles.popoverMessage}>
          <MaterialIcon
            icon="error"
            className={styles.errorIcon}
            symbolsOutlined
            size="md-20"
          />
          <Typography variant="body">{label}</Typography>
        </div>
        <div className={styles.popoverActions}>
          <Button
            buttonText={t('dismiss')}
            buttonType={ButtonTypes.secondaryOutlined}
            buttonSize={ButtonSizes.small}
            onClick={onCancel}
          />
          {showRetry && (
            <Button
              buttonText={t('try-again')}
              buttonType={ButtonTypes.secondaryOutlined}
              buttonSize={ButtonSizes.small}
              leftIcon="autorenew"
              disabled={!!blockedText}
              onClick={onRetry}
            />
          )}
        </div>
      </div>
      <CreditsFooter credits={creditsRemaining} blockedText={blockedText} />
    </div>
  );
}
